import {
  PRIORITY_FACTOR_LABELS,
  PRIORITY_TIERS,
  PRIORITY_TIER_HELP,
  formatDate,
  formatNumber,
  type PriorityDto,
  type PriorityFactorKey,
  type PriorityTier,
  type PriorityWeightsDto,
} from '@nova/shared';
import { ChevronDown, ListFilter, RefreshCw, Search, SlidersHorizontal, Target } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/Button.js';
import { EmptyState } from '../../components/EmptyState.js';
import { GlassPanel } from '../../components/GlassPanel.js';
import { SkeletonPanel } from '../../components/Skeleton.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { ApiError } from '../../lib/api.js';
import { cn } from '../../lib/cn.js';
import { ExpiryBadge, Field, SelectInput, TextArea, TextInput } from './marketWidgets.js';
import { FactorBreakdown, ScoreBar, TierChip } from './priorityWidgets.js';
import { useMarketFacets } from './useMarket.js';
import {
  usePriorities,
  usePriorityWeights,
  useRecompute,
  useSetTier,
  useUpdateWeights,
  type PriorityFilters,
  type WeightsInput,
} from './usePriority.js';

const FACTOR_KEYS: PriorityFactorKey[] = ['contractExpiry', 'size', 'fit', 'relationship', 'signals'];

export function PrioriteringPage() {
  const isAdmin = useAuth().hasModule('SALES', 'ADMIN');
  const { data: facets } = useMarketFacets();
  const recompute = useRecompute();
  const [filters, setFilters] = useState<PriorityFilters>({ sort: 'tier' });
  const [showWeights, setShowWeights] = useState(false);

  // Default to the first product once facets load (the list is org × product).
  useEffect(() => {
    if (!filters.productId && facets?.products?.[0]) {
      setFilters((f) => ({ ...f, productId: facets.products[0]!.id }));
    }
  }, [facets, filters.productId]);

  const { data, isLoading } = usePriorities(filters);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-ink flex items-center gap-2 text-xl">
            <Target size={20} aria-hidden /> Prioritering
          </h1>
          <p className="text-ink-soft mt-0.5 text-sm">
            Hvem ringer vi? En forklarbar score per organisasjon og produkt — med manuell tier som
            alltid vinner.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button
              size="sm"
              variant={showWeights ? 'primary' : 'secondary'}
              icon={<SlidersHorizontal size={14} />}
              onClick={() => setShowWeights((v) => !v)}
            >
              Vekter
            </Button>
          )}
          <Button
            size="sm"
            variant="secondary"
            icon={<RefreshCw size={14} />}
            loading={recompute.isPending}
            onClick={() => recompute.mutate(filters.productId)}
          >
            Rekjør score
          </Button>
        </div>
      </header>

      {isAdmin && showWeights && (
        <WeightEditor productId={filters.productId} onClose={() => setShowWeights(false)} />
      )}

      <FilterBar facets={facets} filters={filters} setFilters={setFilters} />

      {isLoading ? (
        <SkeletonPanel lines={6} />
      ) : !data || data.items.length === 0 ? (
        <GlassPanel>
          <EmptyState
            title="Ingen prioriteter å vise."
            description="Rekjør scoren, eller importer og registrer produktbeholdning først."
          />
        </GlassPanel>
      ) : (
        <>
          <p className="text-ink-soft text-xs">
            {data.total} rader
            {data.overriddenCount > 0 ? ` · ${data.overriddenCount} manuelt overstyrt` : ''}
          </p>
          <ol className="space-y-2">
            {data.items.map((p, i) => (
              <PriorityRow key={`${p.organizationId}:${p.productId}`} rank={i + 1} priority={p} isAdmin={isAdmin} />
            ))}
          </ol>
        </>
      )}
    </div>
  );
}

function FilterBar({
  facets,
  filters,
  setFilters,
}: {
  facets: ReturnType<typeof useMarketFacets>['data'];
  filters: PriorityFilters;
  setFilters: React.Dispatch<React.SetStateAction<PriorityFilters>>;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative">
        <Search size={14} className="text-ink-soft absolute left-2.5 top-1/2 -translate-y-1/2" aria-hidden />
        <input
          className="border-hairline bg-base/40 text-ink w-52 rounded-lg border py-1.5 pl-8 pr-3 text-sm"
          placeholder="Søk organisasjon"
          value={filters.search ?? ''}
          onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value || undefined }))}
        />
      </div>
      {(facets?.products.length ?? 0) > 1 && (
        <FilterSelect
          value={filters.productId ?? ''}
          onChange={(v) => setFilters((f) => ({ ...f, productId: v || undefined }))}
          options={(facets?.products ?? []).map((p) => [p.id, p.name] as [string, string])}
        />
      )}
      <FilterSelect
        value={filters.marketId ?? ''}
        onChange={(v) => setFilters((f) => ({ ...f, marketId: v || undefined }))}
        options={[['', 'Alle markeder'], ...(facets?.markets ?? []).map((m) => [m.id, m.name] as [string, string])]}
      />
      <FilterSelect
        value={filters.tier ?? ''}
        onChange={(v) => setFilters((f) => ({ ...f, tier: v || undefined }))}
        options={[['', 'Alle tiers'], ...PRIORITY_TIERS.map((t) => [t, `Tier ${t}`] as [string, string])]}
      />
      <FilterSelect
        value={filters.sort ?? 'tier'}
        onChange={(v) => setFilters((f) => ({ ...f, sort: v as PriorityFilters['sort'] }))}
        options={[
          ['tier', 'Sorter: prioritet'],
          ['score', 'Sorter: score'],
          ['expiry', 'Sorter: utløper snart'],
        ]}
      />
      <button
        onClick={() => setFilters((f) => ({ ...f, overriddenOnly: f.overriddenOnly ? undefined : true }))}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs transition-colors',
          filters.overriddenOnly
            ? 'bg-accent/15 text-accent border-accent/30'
            : 'border-hairline text-ink-soft hover:text-ink',
        )}
      >
        <ListFilter size={13} aria-hidden /> Kun overstyrte
      </button>
    </div>
  );
}

function PriorityRow({ rank, priority, isAdmin }: { rank: number; priority: PriorityDto; isAdmin: boolean }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const holding = priority.holding;

  return (
    <li className="border-hairline bg-panel/60 rounded-lg border">
      <div className="flex items-start gap-3 p-3">
        <span className="text-ink-soft/60 w-6 shrink-0 pt-0.5 text-right text-sm tabular-nums">{rank}</span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <button
              className="text-ink hover:text-accent truncate text-sm font-medium"
              onClick={() => navigate(`/marked/${priority.organizationId}`)}
              title="Åpne organisasjonen"
            >
              {priority.organizationName}
            </button>
            <TierChip tier={priority.effectiveTier} overridden={priority.manualTier !== null} size="sm" />
            {priority.isSeed && <span className="text-ink-soft text-[10px]">demo</span>}
          </div>
          <p className="text-ink-soft mt-0.5 text-xs">
            {priority.productName}
            {priority.fylke ? ` · ${priority.fylke}` : ''}
            {priority.population ? ` · ${formatNumber(priority.population)} innb.` : ''}
            {holding?.competitor ? ` · ${holding.competitor}` : ''}
          </p>
          {priority.rationale && (
            <p className="text-ink-soft/80 mt-1 text-[11px] leading-relaxed">{priority.rationale}</p>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <ScoreBar score={priority.computedScore} />
          {holding && (
            <ExpiryBadge
              contractEnd={holding.contractEnd}
              daysToExpiry={holding.daysToExpiry}
              formatted={holding.contractEnd ? formatDate(holding.contractEnd) : ''}
            />
          )}
          <button
            className="text-ink-soft hover:text-ink inline-flex items-center gap-1 text-[11px]"
            onClick={() => setOpen((v) => !v)}
          >
            Detaljer <ChevronDown size={12} className={cn('transition-transform', open && 'rotate-180')} aria-hidden />
          </button>
        </div>
      </div>

      {open && (
        <div className="border-hairline grid gap-4 border-t p-3 md:grid-cols-2">
          <div>
            <p className="label-micro text-ink-soft mb-2">Faktorbrytning</p>
            <FactorBreakdown factors={priority.factors} />
          </div>
          <div>
            {isAdmin ? (
              <TierOverride priority={priority} />
            ) : priority.manualTier ? (
              <OverrideSummary priority={priority} />
            ) : (
              <p className="text-ink-soft/70 text-xs">Ingen manuell overstyring satt.</p>
            )}
          </div>
        </div>
      )}
    </li>
  );
}

function OverrideSummary({ priority }: { priority: PriorityDto }) {
  return (
    <div className="border-hairline bg-base/30 rounded-lg border p-3 text-xs">
      <div className="flex items-center gap-2">
        <TierChip tier={priority.manualTier!} overridden size="sm" />
        <span className="text-ink-soft">
          satt av {priority.tierSetByName ?? 'ukjent'}
          {priority.tierSetAt ? ` · ${formatDate(priority.tierSetAt)}` : ''}
        </span>
      </div>
      {priority.tierReason && <p className="text-ink-soft mt-1.5 leading-relaxed">{priority.tierReason}</p>}
    </div>
  );
}

function TierOverride({ priority }: { priority: PriorityDto }) {
  const setTier = useSetTier();
  const [tier, setTier_] = useState<string>(priority.manualTier ?? '');
  const [reason, setReason] = useState(priority.tierReason ?? '');

  function save() {
    setTier.mutate({
      organizationId: priority.organizationId,
      productId: priority.productId,
      tier: (tier || null) as PriorityTier | null,
      reason: reason.trim() || null,
    });
  }

  return (
    <div className="border-accent/30 bg-base/40 space-y-3 rounded-lg border p-3">
      <p className="label-micro text-ink-soft">Manuell tier (vinner over scoren)</p>
      <Field label="Tier">
        <SelectInput value={tier} onChange={(e) => setTier_(e.target.value)}>
          <option value="">— ingen (bruk beregnet {priority.derivedTier}) —</option>
          {PRIORITY_TIERS.map((t) => (
            <option key={t} value={t}>
              Tier {t} — {PRIORITY_TIER_HELP[t]}
            </option>
          ))}
        </SelectInput>
      </Field>
      <Field label="Begrunnelse" hint="Hvorfor overstyrer vi den beregnede scoren?">
        <TextArea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="primary" loading={setTier.isPending} onClick={save}>
          Lagre tier
        </Button>
        {setTier.isSuccess && <span className="text-success text-xs">Lagret.</span>}
        {setTier.isError && <span className="text-danger text-xs">{(setTier.error as ApiError).message}</span>}
      </div>
    </div>
  );
}

// ── Vekteditor ──────────────────────────────────────────────────────────────

const DEFAULT_WEIGHTS: WeightsInput = {
  contractExpiry: 30,
  size: 15,
  fit: 25,
  relationship: 15,
  signals: 15,
};

function WeightEditor({ productId, onClose }: { productId: string | undefined; onClose: () => void }) {
  const { data: allWeights } = usePriorityWeights();
  const current: PriorityWeightsDto | undefined = productId
    ? allWeights?.find((w) => w.productId === productId)
    : allWeights?.[0];
  const update = useUpdateWeights(current?.productId ?? productId ?? '');
  const [weights, setWeights] = useState<WeightsInput>(DEFAULT_WEIGHTS);

  useEffect(() => {
    if (current) {
      setWeights({
        contractExpiry: current.contractExpiry,
        size: current.size,
        fit: current.fit,
        relationship: current.relationship,
        signals: current.signals,
      });
    }
  }, [current]);

  const total = FACTOR_KEYS.reduce((s, k) => s + (weights[k] || 0), 0);

  return (
    <GlassPanel>
      <div className="space-y-4 p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-ink text-sm font-medium">Faktorvekter — {current?.productName ?? 'produkt'}</p>
            <p className="text-ink-soft text-xs">
              Vektene styrer hvor mye hver faktor teller. De normaliseres, så forholdet mellom dem er
              det som betyr noe (sum nå: {total}).
            </p>
          </div>
          {current?.updatedByName && (
            <span className="text-ink-soft/60 text-[11px]">Sist endret av {current.updatedByName}</span>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {FACTOR_KEYS.map((key) => (
            <Field key={key} label={PRIORITY_FACTOR_LABELS[key]}>
              <TextInput
                type="number"
                min={0}
                value={String(weights[key])}
                onChange={(e) => setWeights((w) => ({ ...w, [key]: Number(e.target.value) || 0 }))}
              />
            </Field>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="primary"
            loading={update.isPending}
            disabled={!current}
            onClick={() => update.mutate(weights, { onSuccess: onClose })}
          >
            Lagre og rekjør
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setWeights(DEFAULT_WEIGHTS)}>
            Tilbakestill til standard
          </Button>
          <Button size="sm" variant="ghost" onClick={onClose}>
            Lukk
          </Button>
          {update.isError && <span className="text-danger text-xs">{(update.error as ApiError).message}</span>}
        </div>
      </div>
    </GlassPanel>
  );
}

function FilterSelect({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: [string, string][];
}) {
  return (
    <select
      className="border-hairline bg-base/40 text-ink rounded-lg border px-3 py-1.5 text-sm"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {options.map(([v, label]) => (
        <option key={v} value={v}>
          {label}
        </option>
      ))}
    </select>
  );
}
